import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import './FoundersPage.css';

const FoundersPage = () => {
  const founders = [
    {
      name: 'Greg Herlein',
      role: 'Co-Founder',
      image: '/images/founders/greg.jpg',
      linkedin: 'https://www.linkedin.com/in/gherlein/',
      bio: [
        "I started my career as a US Navy Submarine Nuclear Power Plant Operator and then Supervisor. When you're responsible for a nuclear reactor hundreds of feet underwater, you learn quickly about inter-related systems. Those lessons never left me.",
        "After the Navy, I spent decades in Silicon Valley leading engineering teams at companies you've heard of—Rackspace, Cisco, AWS — and plenty of startups you haven't. I built distributed systems before \"distributed systems\" was a buzzword. I learned what works at scale and what doesn't.",
        "But robotics was always my passion on the side. I coached middle school and high school robotics teams in FIRST LEGO League and VEX competitions, watching students struggle with the same complexities that frustrate professional engineers. At home, I built robots for fun—and ran into those same frustrations myself.",
        "GoRAI grew from a simple question: why is robotics software so much harder than it needs to be? The distributed systems lessons from my career, the redundancy requirements from nuclear power, and the accessibility needs from coaching young roboticists—they all pointed to the same answer. We needed something new."
      ]
    },
    {
      name: 'Luca Herlein',
      role: 'Co-Founder',
      image: '/images/founders/luca.png',
      linkedin: 'https://www.linkedin.com/in/luca-herlein-4023711ba/',
      bio: [
        "I have built robots my entire life. In Kindergarten I'd wake up early to build robots with my Dad.  In middle and high school I competed in FLL and VEX robotics. In college I built advanced competition fliers in international collegiate competition. I understand what it means to build robots, and I know the challenges.",
        "I studied Aerospace Engineering at CU Boulder, where I learned the formal foundations—dynamics, control systems, calculus, etc. I served as Aerodynamics Lead Engineer on the university's 2021-22 Design Build Fly (DBF) competition team, applying those foundations to aircraft that had to actually fly. I served as the Navigation Team Lead on my Senior Design Project team, running systems of systems design addressing logistical needs in the cislunar regime.",
        "After graduating I decided to go into Software Engineering and currently work as a developer relations Engineer. I've worked with engineers rolling out tens of thousands of IOT devices and seen the challenges of modern software and hardware infrastructure.",
        "Software alone didn't keep my attention for long, and I've spent the last two years building robots of all kinds. I've built custom multi-rotor and fixed wing aircraft and ground drones. I've built robot arms and powered them with AI vision. Now it's time to productize the learning I've been doing."]
    }
  ];

  return (
    <div className="founders-page">
      {/* Background */}
      <div className="founders-bg">
        <div className="bg-gradient" />
        <div className="grid-pattern" />
        <div className="glow-orb orb-1" />
        <div className="glow-orb orb-2" />
      </div>

      {/* Hero */}
      <section className="founders-hero">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="section-label">The Team</span>
            <h1>Meet the Founders</h1>
            <p className="hero-subtitle">
              A father-son team combining decades of industry experience with competition robotics expertise.
              We're building the robotics tools we wish we had.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Founders */}
      <section className="founders-content">
        <div className="section-container">
          {founders.map((founder, index) => (
            <motion.div
              key={founder.name}
              className="founder-detail-card"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + index * 0.15, duration: 0.6 }}
            >
              <div className="founder-header">
                <div className="founder-photo">
                  <img 
                    src={founder.image} 
                    alt={founder.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="photo-placeholder">
                    <span>{founder.name.split(' ').map(n => n[0]).join('')}</span>
                  </div>
                </div>
                <div className="founder-intro">
                  <h2>{founder.name}</h2>
                  <span className="founder-role">{founder.role}</span>
                  <a 
                    href={founder.linkedin} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="linkedin-link"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    Connect on LinkedIn
                  </a>
                </div>
              </div>

              <div className="founder-story">
                {founder.bio.map((paragraph, i) => (
                  <p key={i}>{paragraph}</p>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Why Section */}
      <section className="why-section">
        <div className="section-container">
          <motion.div
            className="why-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Why We're Building This</h2>
            <p>
              Between coaching robotics teams and building robots ourselves, we've spent years 
              watching people struggle with software that's either too simple to do real work 
              or too complex to learn. We've seen brilliant students give up because ROS2 was 
              impenetrable. We've seen makers abandon projects because Arduino hit its limits.
            </p>
            <p>
              GoRAI and Emerging Robotics exist because we believe the gap between "educational toy" 
              and "professional platform" shouldn't be a chasm. You shouldn't need a PhD to write 
              robot code. You shouldn't need $15,000 to deploy a marine robot.
            </p>
            <p>
              <strong>We're building the tools we wish we had.</strong>
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="founders-cta-section">
        <div className="section-container">
          <motion.div
            className="cta-box"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Join Us</h2>
            <p>
              Whether you want to build with us, contribute to GoRAI, or just follow along—we'd love to have you.
            </p>
            <div className="cta-buttons">
              <a 
                href="https://github.com/emergingrobotics/gorai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="cta-primary"
              >
                ⭐ Star on GitHub
              </a>
              <Link to="/gorai" className="cta-secondary">
                Explore Gorai
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Back Link */}
      <div className="section-container">
        <motion.div
          className="back-link"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <Link to="/">← Back to Home</Link>
        </motion.div>
      </div>
    </div>
  );
};

export default FoundersPage;
